import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IntakePreferenceSection } from '@/services/types/firms-types';

// Suggested section titles
const SUGGESTED_TITLES = [
  'Intake Requirements',
  'Required Documentation',
  'Systems',
  'Firm Info',
];

interface EditSectionDialogProps {
  section: IntakePreferenceSection;
  onSave: (section: IntakePreferenceSection) => void;
  triggerLabel?: string;
}

export const EditSectionDialog: React.FC<EditSectionDialogProps> = ({
  section,
  onSave,
  triggerLabel = 'Edit',
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(section.title);
  const [content, setContent] = useState(section.content);

  const handleSave = (): void => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    onSave({ title: trimmedTitle, content: content.trim() });
    setOpen(false);
  };

  const handleCancel = (): void => {
    setOpen(false);
    setTitle(section.title);
    setContent(section.content);
  };

  const handleOpenChange = (newOpen: boolean): void => {
    if (newOpen) {
      setTitle(section.title);
      setContent(section.content);
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Section</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label htmlFor="edit_section_title">Section Title</Label>
            <Input
              id="edit_section_title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Intake Requirements"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs text-gray-500">Quick fills:</span>
              {SUGGESTED_TITLES.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setTitle(suggestion)}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </Field>
          <Field>
            <Label htmlFor="edit_section_content">Content</Label>
            <Textarea
              id="edit_section_content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              placeholder="Enter section content..."
            />
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!title.trim()}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
